import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useAthlete } from '@/context/AthleteContext';
import { useState, useEffect } from 'react';
import { 
  LayoutDashboard, Target, Video, ClipboardList, Calendar, LogOut, Menu, X, Zap, FileText, 
  Lock, ChevronDown, Shield, Settings, MessageSquare, Home, BarChart3, Users, User, Trophy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import NotificationCenter from '@/components/NotificationCenter';
import NotificationBell from '@/components/NotificationBell';
import BetaBanner from '@/components/BetaBanner';
import FoundingWelcome from '@/components/FoundingWelcome';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ParentLayout = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const { theme } = useTheme();
  const { athlete, athletePass, athletes, selectedAthleteIndex, selectAthlete, loading, hasMultipleAthletes } = useAthlete();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAthleteDropdown, setShowAthleteDropdown] = useState(false);
  const [showContextDropdown, setShowContextDropdown] = useState(false);
  const [availableContexts, setAvailableContexts] = useState([]);
  const [currentContext, setCurrentContext] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const res = await axios.get(`${API}/conversations`, { headers: getAuthHeaders() });
        const total = res.data.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
        setUnreadCount(total);
      } catch (err) {
        console.error('Failed to fetch unread count:', err);
      }
    };
    
    if (user) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, getAuthHeaders]);

  // Check if we're in team context
  const isTeamContext = location.pathname.includes('/dashboard/team/');
  const teamIdMatch = location.pathname.match(/\/dashboard\/team\/([^\/]+)/);
  const currentTeamId = teamIdMatch ? teamIdMatch[1] : null;

  // Fetch available contexts (private coach + teams)
  useEffect(() => {
    const fetchContexts = async () => {
      if (!athlete) return;
      
      const contexts = [];
      
      // Add Private Coach context if athlete has one
      if (athlete.coach_id) {
        contexts.push({
          id: 'private',
          type: 'private',
          name: athlete.coach_name || 'Private Coach',
          icon: User,
          path: '/dashboard'
        });
      }
      
      // Fetch teams using same endpoint as Dashboard
      try {
        const teamsRes = await axios.get(`${API}/parent/athlete/${athlete.id}/teams`, { headers: getAuthHeaders() });
        // teamsRes.data is an array directly (not { teams: [] })
        const teamsData = Array.isArray(teamsRes.data) ? teamsRes.data : (teamsRes.data?.teams || []);
        
        if (teamsData && teamsData.length > 0) {
          teamsData.forEach(team => {
            contexts.push({
              id: team.team_id,
              type: 'team',
              name: team.team_name,
              coachName: team.coach_name,
              icon: Users,
              path: `/dashboard/team/${team.team_id}`
            });
          });
        }
      } catch (err) {
        // Silently ignore
      }
      
      setAvailableContexts(contexts);
      
      // Set current context
      if (isTeamContext && currentTeamId) {
        const teamContext = contexts.find(c => c.id === currentTeamId);
        setCurrentContext(teamContext || contexts[0]);
      } else {
        const privateContext = contexts.find(c => c.type === 'private');
        setCurrentContext(privateContext || contexts[0]);
      }
    };
    
    fetchContexts();
  }, [athlete, isTeamContext, currentTeamId]);

  const handleLogout = () => {
    logout();
    navigate('/unified-login');
  };

  const hasActivePass = athletePass?.status === 'active';

  // Global items (available in all contexts)
  const globalNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Overview', alwaysAccessible: true, exact: true },
    { path: '/dashboard/weekly-challenge', icon: Trophy, label: 'Weekly Challenge', alwaysAccessible: true, isGlobal: true, badge: 'NEW' },
    { path: '/dashboard/development-profile', icon: BarChart3, label: 'Development Profile', alwaysAccessible: false, isGlobal: true },
    { path: '/dashboard/calendar', icon: Calendar, label: 'Calendar', alwaysAccessible: true },
    { path: '/dashboard/settings', icon: Settings, label: 'Settings', alwaysAccessible: true },
  ];

  // Context-specific items for Private Coach
  const privateCoachItems = [
    { path: '/dashboard/goals', icon: Target, label: 'My Goals', alwaysAccessible: false },
    { path: '/dashboard/train-at-home', icon: Home, label: 'Train at Home', alwaysAccessible: false },
    { path: '/dashboard/technical', icon: Target, label: 'Technical Dev', alwaysAccessible: false },
    { path: '/dashboard/training-plan', icon: ClipboardList, label: 'Training Plan', alwaysAccessible: false },
    { path: '/dashboard/videos', icon: Video, label: 'Videos', alwaysAccessible: false },
    { path: '/dashboard/sessions', icon: Calendar, label: 'Sessions', alwaysAccessible: false },
    { path: '/dashboard/messages', icon: MessageSquare, label: 'Messages', alwaysAccessible: false, unreadBadge: true },
    { path: '/dashboard/monthly-report', icon: FileText, label: 'Monthly Report', alwaysAccessible: false },
  ];

  // Context-specific items for Team Coach
  const teamCoachItems = [
    { path: `/dashboard/team/${currentTeamId}/technical`, icon: Target, label: 'Technical Dev', alwaysAccessible: true },
    { path: `/dashboard/team/${currentTeamId}/training`, icon: ClipboardList, label: 'Training Plan', alwaysAccessible: true },
    { path: `/dashboard/team/${currentTeamId}/sessions`, icon: Calendar, label: 'Sessions', alwaysAccessible: true },
    { path: `/dashboard/team/${currentTeamId}/videos`, icon: Video, label: 'Videos', alwaysAccessible: true },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const canAccess = (item) => {
    return item.alwaysAccessible || hasActivePass;
  };

  const handleNavClick = (e, item) => {
    if (!canAccess(item)) {
      e.preventDefault();
      navigate('/dashboard');
    }
    setSidebarOpen(false);
  };

  const handleAthleteSelect = (index) => {
    selectAthlete(index);
    setShowAthleteDropdown(false);
    navigate('/dashboard');
  };

  const handleContextSelect = (context) => {
    setCurrentContext(context);
    setShowContextDropdown(false);
    navigate(context.path);
  };

  const renderNavItem = (item) => {
    const accessible = canAccess(item);
    const active = isActive(item.path, item.exact);
    
    return (
      <Link
        key={item.path}
        to={accessible ? item.path : '#'}
        onClick={(e) => handleNavClick(e, item)}
        className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
          active
            ? 'bg-primary/10 text-primary'
            : accessible
              ? 'text-muted-foreground hover:bg-muted hover:text-foreground'
              : 'text-muted-foreground/50 cursor-not-allowed'
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="relative">
          <item.icon size={20} className={!accessible ? 'opacity-50' : ''} />
          {/* Unread message badge */}
          {item.unreadBadge && unreadCount > 0 && (
            <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>
        <span className={`text-sm uppercase tracking-wider ${!accessible ? 'opacity-50' : ''}`}>
          {item.label}
        </span>
        {item.isGlobal && (
          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-primary/20 text-primary">{item.badge || 'AI'}</span>
        )}
        {!accessible && !item.isGlobal && (
          <Lock size={12} className="ml-auto text-yellow-500/70" />
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Founding Beta Banner */}
      <BetaBanner />
      
      <div className="flex flex-1">
        {/* Mobile sidebar toggle */}
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="lg:hidden fixed top-16 right-4 z-50 p-2.5 bg-card border border-border rounded-sm shadow-lg"
          data-testid="parent-mobile-menu-btn"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-40 top-8 lg:top-0
          w-64 bg-card border-r border-border
          transform transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <Link to="/dashboard" className="flex items-center gap-2 min-w-0 flex-1">
                  {theme.logoUrl ? (
                    <img src={theme.logoUrl} alt="Logo" className="h-8 w-auto" />
                  ) : (
                    <img src="/brand/meybra-logo.png" alt="Meybra" className="h-8 w-auto flex-shrink-0" data-testid="brand-logo" />
                  )}
                  <span className="font-heading text-lg truncate">{theme.businessName}</span>
                </Link>
                <NotificationCenter />
              </div>
              <div className="mt-3">
                <BetaBanner variant="inline" />
              </div>
            </div>

            {/* Athlete Selector (for parents with multiple athletes) */}
            {hasMultipleAthletes && athlete && (
              <div className="mx-4 mt-4 relative">
                <button
                  onClick={() => setShowAthleteDropdown(!showAthleteDropdown)}
                  className="w-full p-3 bg-primary/10 border border-primary/30 rounded-sm flex items-center justify-between hover:bg-primary/20 transition-colors"
                  data-testid="sidebar-athlete-selector-btn"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-bold text-sm">{athlete.name?.[0]}</span>
                    </div>
                    <span className="font-medium truncate">{athlete.name}</span>
                  </div>
                  <ChevronDown size={16} className={`text-primary transition-transform ${showAthleteDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showAthleteDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-sm shadow-lg z-50">
                    {athletes.map((ath, index) => (
                      <button
                        key={ath.id}
                        onClick={() => handleAthleteSelect(index)}
                        className={`w-full p-3 flex items-center gap-3 hover:bg-muted transition-colors ${
                          index === selectedAthleteIndex ? 'bg-primary/10' : ''
                        }`}
                      >
                        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-primary font-bold text-sm">{ath.name?.[0]}</span>
                        </div>
                        <span className={`truncate ${index === selectedAthleteIndex ? 'font-medium text-primary' : ''}`}>
                          {ath.name}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Training Context Selector - ALWAYS VISIBLE when there are multiple contexts */}
            {availableContexts.length > 1 && (
              <div className="mx-4 mt-4 relative">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 px-1">Training Context</p>
                <button
                  onClick={() => setShowContextDropdown(!showContextDropdown)}
                  className={`w-full p-3 border rounded-sm flex items-center justify-between hover:border-primary/50 transition-colors ${
                    isTeamContext 
                      ? 'bg-blue-500/10 border-blue-500/30' 
                      : 'bg-emerald-500/10 border-emerald-500/30'
                  }`}
                  data-testid="training-context-selector"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {currentContext?.icon && (
                      <currentContext.icon size={18} className={isTeamContext ? 'text-blue-400' : 'text-emerald-400'} />
                    )}
                    <div className="text-left min-w-0">
                      <p className={`text-sm font-medium truncate ${isTeamContext ? 'text-blue-400' : 'text-emerald-400'}`}>
                        {currentContext?.name || 'Select Context'}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {currentContext?.type === 'private' ? 'Private Training' : 'Team Training'}
                      </p>
                    </div>
                  </div>
                  <ChevronDown size={16} className={`transition-transform ${showContextDropdown ? 'rotate-180' : ''} ${isTeamContext ? 'text-blue-400' : 'text-emerald-400'}`} />
                </button>
                
                {showContextDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-sm shadow-lg z-50 max-h-64 overflow-y-auto">
                    <p className="px-3 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                      Switch Training Context
                    </p>
                    {availableContexts.map((context) => (
                      <button
                        key={context.id}
                        onClick={() => handleContextSelect(context)}
                        className={`w-full p-3 flex items-center gap-3 hover:bg-muted transition-colors ${
                          currentContext?.id === context.id ? 'bg-primary/10' : ''
                        }`}
                      >
                        <context.icon size={18} className={context.type === 'team' ? 'text-blue-400' : 'text-emerald-400'} />
                        <div className="text-left min-w-0">
                          <p className="text-sm font-medium truncate">{context.name}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {context.type === 'private' ? 'Private Coach' : context.coachName || 'Team Coach'}
                          </p>
                        </div>
                        {currentContext?.id === context.id && (
                          <span className="ml-auto text-xs text-primary">Active</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Pass Status Indicator - Only show in private context */}
            {!loading && !hasActivePass && !isTeamContext && (
              <div className="mx-4 mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                <div className="flex items-center gap-2 text-yellow-400">
                  <Lock size={14} />
                  <span className="text-xs font-medium">Pass Required</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Activate your Athlete Pass to access all features
                </p>
              </div>
            )}

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
              {/* Global Items */}
              <div className="mb-2">
                {globalNavItems.slice(0, 2).map(renderNavItem)}
              </div>
              
              {/* Divider */}
              <div className="my-3 px-4">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-border"></div>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {isTeamContext ? 'Team' : 'Private'} Training
                  </span>
                  <div className="flex-1 h-px bg-border"></div>
                </div>
              </div>
              
              {/* Context-specific Items */}
              {isTeamContext ? (
                teamCoachItems.map(renderNavItem)
              ) : (
                privateCoachItems.map(renderNavItem)
              )}
              
              {/* Divider */}
              <div className="my-3 px-4">
                <div className="h-px bg-border"></div>
              </div>
              
              {/* Global Items - Calendar & Settings */}
              {globalNavItems.slice(2).map(renderNavItem)}
            </nav>

            {/* User section — flex-shrink-0 keeps logout always reachable on PWA */}
            <div className="p-4 border-t border-border flex-shrink-0">
              <div className="mb-4 px-4">
                <p className="text-sm font-medium truncate">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
              <Button 
                variant="ghost" 
                className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
                onClick={handleLogout}
              >
                <LogOut size={20} />
                <span className="text-sm uppercase tracking-wider">Logout</span>
              </Button>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-30"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 lg:ml-0 min-h-screen overflow-x-hidden">
          <div className="hidden lg:flex justify-end items-center px-6 pt-4">
            <NotificationBell
              tokenKey="parent_token"
              onItemClick={() => navigate('/dashboard/messages')}
            />
          </div>
          <div className="lg:hidden fixed top-16 right-16 z-50">
            <NotificationBell
              tokenKey="parent_token"
              onItemClick={() => navigate('/dashboard/messages')}
            />
          </div>
          <div className="p-4 md:p-6 lg:p-8 pt-6 lg:pt-2">
            <Outlet />
          </div>
        </main>
      </div>
      <FoundingWelcome userId={user?.id} role="parent" />
    </div>
  );
};

export default ParentLayout;
